/**
 * Coupon Calculations
 *
 * Logique de calcul des réductions et commissions
 *
 * RÈGLES:
 * - La réduction s'applique UNIQUEMENT sur le prix de base du service
 * - La commission partenaire s'applique UNIQUEMENT sur le prix de base du service
 * - Les options et le transport ne sont PAS affectés par le coupon
 *
 * TYPES DE COUPONS:
 * - PARTNER: member_discount_pct + partner_commission_pct
 * - USER: user_discount_pct + partner_commission_pct (si partenaire lié)
 */

export type CouponType = 'PARTNER' | 'USER'

export type CouponCalculationInput = {
  serviceBasePrice: number
  optionsPrice: number
  transportPrice: number
  memberDiscountPct: number // 0-100 (PARTNER type)
  partnerCommissionPct: number // 0-100
  userDiscountPct?: number // 0-100 (USER type)
  couponType?: CouponType
  mode?: 'PARTNER' | 'PUBLIC'
}

export type CouponCalculationResult = {
  originalServicePrice: number
  memberDiscountAmount: number
  userDiscountAmount: number
  discountAmount: number // combined discount
  partnerCommissionAmount: number
  finalServicePrice: number
  finalTotalPrice: number
  breakdown: {
    service: number
    options: number
    transport: number
    discount: number
    total: number
  }
}

/**
 * Calcule tous les montants liés à un coupon.
 * Compatible avec les deux types PARTNER et USER.
 */
export const calculateCouponPricing = (
  input: CouponCalculationInput
): CouponCalculationResult => {
  const {
    serviceBasePrice,
    optionsPrice,
    transportPrice,
    memberDiscountPct,
    partnerCommissionPct,
    userDiscountPct = 0,
    couponType,
    mode,
  } = input

  // Déterminer la réduction applicable
  let effectiveDiscountPct = 0
  if (couponType === 'USER' || mode === 'PUBLIC') {
    effectiveDiscountPct = userDiscountPct
  } else {
    effectiveDiscountPct = memberDiscountPct
  }

  // Calcul de la réduction (sur le service uniquement)
  const memberDiscountAmount = (serviceBasePrice * memberDiscountPct) / 100
  const userDiscountAmount = (serviceBasePrice * userDiscountPct) / 100
  const discountAmount = (serviceBasePrice * effectiveDiscountPct) / 100

  // Calcul de la commission partenaire (sur le service uniquement)
  const partnerCommissionAmount = (serviceBasePrice * partnerCommissionPct) / 100

  // Prix du service après réduction
  const finalServicePrice = serviceBasePrice - discountAmount

  // Prix total final
  const finalTotalPrice = finalServicePrice + optionsPrice + transportPrice

  return {
    originalServicePrice: serviceBasePrice,
    memberDiscountAmount: round(memberDiscountAmount, 2),
    userDiscountAmount: round(userDiscountAmount, 2),
    discountAmount: round(discountAmount, 2),
    partnerCommissionAmount: round(partnerCommissionAmount, 2),
    finalServicePrice: round(finalServicePrice, 2),
    finalTotalPrice: round(finalTotalPrice, 2),
    breakdown: {
      service: round(finalServicePrice, 2),
      options: round(optionsPrice, 2),
      transport: round(transportPrice, 2),
      discount: round(-discountAmount, 2),
      total: round(finalTotalPrice, 2),
    },
  }
}

/**
 * computePricing — Fonction centralisée de calcul de pricing.
 *
 * Utilisable côté client pour affichage, le backend recalcule de son côté.
 */
export const computePricing = (params: {
  baseServicePrice: number
  optionsPrice: number
  coupon: {
    type?: CouponType
    partner_commission_pct?: number
    member_discount_pct?: number
    user_discount_pct?: number | null
  } | null
  mode: 'PARTNER' | 'PUBLIC'
}): {
  original_service_price: number
  discount_amount: number
  commission_amount: number
  final_service_price: number
  options_price: number
  final_total: number
} => {
  const { baseServicePrice, optionsPrice, coupon, mode } = params

  if (!coupon) {
    return {
      original_service_price: baseServicePrice,
      discount_amount: 0,
      commission_amount: 0,
      final_service_price: baseServicePrice,
      options_price: optionsPrice,
      final_total: round(baseServicePrice + optionsPrice, 2),
    }
  }

  // Déterminer le % de réduction
  let discountPct = 0
  if (mode === 'PARTNER') {
    discountPct = coupon.member_discount_pct ?? 0
  } else {
    discountPct = coupon.user_discount_pct ?? 0
  }

  const commissionPct = coupon.partner_commission_pct ?? 0

  const discountAmount = round((baseServicePrice * discountPct) / 100, 2)
  const commissionAmount = round((baseServicePrice * commissionPct) / 100, 2)
  const finalServicePrice = round(baseServicePrice - discountAmount, 2)

  return {
    original_service_price: baseServicePrice,
    discount_amount: discountAmount,
    commission_amount: commissionAmount,
    final_service_price: finalServicePrice,
    options_price: optionsPrice,
    final_total: round(finalServicePrice + optionsPrice, 2),
  }
}

/**
 * Valide qu'un coupon est applicable
 */
export const validateCouponApplicability = (coupon: {
  valid_from: string
  valid_until: string
  is_active: boolean
  max_usage_count: number | null
  current_usage_count: number
  partner?: { status: string } | null
}): { valid: boolean; error?: string } => {
  const now = new Date()

  // Vérifier que le coupon est actif
  if (!coupon.is_active) {
    return { valid: false, error: 'Ce coupon est désactivé' }
  }

  // Vérifier les dates de validité
  const validFrom = new Date(coupon.valid_from)
  const validUntil = new Date(coupon.valid_until)

  if (now < validFrom) {
    return { valid: false, error: 'Ce coupon n\'est pas encore valide' }
  }

  if (now > validUntil) {
    return { valid: false, error: 'Ce coupon a expiré' }
  }

  // Vérifier le nombre d'utilisations
  if (
    coupon.max_usage_count !== null &&
    coupon.current_usage_count >= coupon.max_usage_count
  ) {
    return { valid: false, error: 'Ce coupon a atteint son nombre maximal d\'utilisations' }
  }

  // Vérifier que le partenaire est actif
  if (coupon.partner && coupon.partner.status !== 'ACTIVE') {
    return { valid: false, error: 'Le partenaire associé à ce coupon est désactivé' }
  }

  return { valid: true }
}

/**
 * Formate un montant en CHF
 */
export const formatPrice = (amount: number): string => {
  return `${amount.toFixed(2)} CHF`
}

/**
 * Arrondit à n décimales
 */
const round = (value: number, decimals: number): number => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

/**
 * Calcule le pourcentage économisé
 */
export const calculateSavingsPercentage = (
  originalTotal: number,
  finalTotal: number
): number => {
  if (originalTotal === 0) return 0
  const savings = originalTotal - finalTotal
  return round((savings / originalTotal) * 100, 1)
}
